import React, { useEffect, useMemo, useState } from "react";

import {
  ICosignUnmappedProperty,
  useCosignProperties,
  useMapExistingCosignProperty,
} from "manager/hooks/api";
import { ICosignProperty } from "manager/hooks/api/integrationQueries";
import { Modal } from "shared/components/Modal/Modal";

import {
  CreatePropertyError,
  EmptyPropertyList,
  PropertyOptionButton,
  PropertyOptionList,
  PropertyOptionMeta,
  PropertyOptionName,
  PropertyOptionSection,
  PropertyOptionSectionTitle,
  PropertyRetryButton,
  PropertySearchInput,
  PropertySummary,
  PropertySummaryLabel,
  PropertySummaryValue,
} from "./styled";

export interface MapExistingCosignPropertyModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => Promise<void> | void;
  property: Pick<ICosignUnmappedProperty, "veroPropertyId" | "name" | "address">;
}

const normalize = (value?: string) => (value || "").trim().toLowerCase();

const getPropertyAddress = (property: ICosignProperty) =>
  property.address ||
  [property.city, property.state, property.zip_code].filter(Boolean).join(", ");

const matchesSearch = (property: ICosignProperty, search: string) => {
  const query = normalize(search);
  if (!query) {
    return true;
  }

  return normalize(property.name).includes(query);
};

const getTokens = (value?: string) =>
  normalize(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const getSimilarityScore = (
  veroProperty: Pick<ICosignUnmappedProperty, "name" | "address">,
  cosignProperty: ICosignProperty
) => {
  const veroName = normalize(veroProperty.name);
  const veroAddress = normalize(veroProperty.address);
  const cosignName = normalize(cosignProperty.name);
  const cosignAddress = normalize(getPropertyAddress(cosignProperty));

  let score = 0;
  if (veroName && cosignName) {
    if (veroName === cosignName) {
      score += 100;
    } else if (veroName.includes(cosignName) || cosignName.includes(veroName)) {
      score += 50;
    }
  }
  if (veroAddress && cosignAddress) {
    if (veroAddress === cosignAddress) {
      score += 80;
    } else if (
      veroAddress.includes(cosignAddress) ||
      cosignAddress.includes(veroAddress)
    ) {
      score += 40;
    }
  }

  const veroTokens = new Set(getTokens(`${veroProperty.name} ${veroProperty.address}`));
  getTokens(`${cosignProperty.name} ${getPropertyAddress(cosignProperty)}`).forEach(
    (token) => {
      if (veroTokens.has(token)) {
        score += 5;
      }
    }
  );

  return score;
};

const extractErrorMessage = (error: any) =>
  error?.errors?.error ||
  error?.errors?.nonFieldErrors?.[0] ||
  "Unable to map the property to Cosign. Please try again.";

const MapExistingCosignPropertyModal: React.FC<
  MapExistingCosignPropertyModalProps
> = ({ open, onCancel, onSuccess, property }) => {
  const [selectedCosignPropertyUuid, setSelectedCosignPropertyUuid] = useState<
    string | null
  >(null);
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isCompletingMapping, setIsCompletingMapping] = useState(false);

  const {
    cosignProperties = [],
    isCosignPropertiesLoading,
    cosignPropertiesError,
    refetchCosignProperties,
  } = useCosignProperties({ enabled: open });
  const {
    mapExistingCosignProperty,
    isMappingExistingCosignProperty,
  } = useMapExistingCosignProperty();

  useEffect(() => {
    if (open) {
      setSelectedCosignPropertyUuid(null);
      setSearch("");
      setFormError(null);
      setIsCompletingMapping(false);
    }
  }, [open, property.veroPropertyId]);

  const rankedProperties = useMemo(
    () =>
      cosignProperties
        .filter((cosignProperty) => matchesSearch(cosignProperty, search))
        .map((cosignProperty, index) => ({
          cosignProperty,
          index,
          score: getSimilarityScore(property, cosignProperty),
        }))
        .sort((a, b) => b.score - a.score || a.index - b.index),
    [cosignProperties, property, search]
  );

  const bestMatch = rankedProperties[0]?.score > 0
    ? rankedProperties[0].cosignProperty
    : undefined;
  const otherProperties = (bestMatch
    ? rankedProperties.slice(1)
    : rankedProperties
  ).map(({ cosignProperty }) => cosignProperty);
  const visibleProperties = rankedProperties.map(
    ({ cosignProperty }) => cosignProperty
  );
  const hasCosignProperties = cosignProperties.length > 0;
  const hasBlockingLoadError = Boolean(
    cosignPropertiesError && !hasCosignProperties
  );
  const shouldShowPropertyOptions =
    !isCosignPropertiesLoading && !hasBlockingLoadError;
  const isSelectedPropertyVisible = visibleProperties.some(
    (cosignProperty) => cosignProperty.uuid === selectedCosignPropertyUuid
  );

  useEffect(() => {
    if (selectedCosignPropertyUuid && !isSelectedPropertyVisible) {
      setSelectedCosignPropertyUuid(null);
    }
  }, [isSelectedPropertyVisible, selectedCosignPropertyUuid]);

  const handleSubmit = () => {
    if (!selectedCosignPropertyUuid) {
      setFormError("Select a Cosign property to continue.");
      return;
    }

    setFormError(null);
    setIsCompletingMapping(true);
    mapExistingCosignProperty(
      {
        vero_property_id: property.veroPropertyId,
        cosign_property_uuid: selectedCosignPropertyUuid,
      },
      {
        onSuccess: async () => {
          try {
            await onSuccess();
            setIsCompletingMapping(false);
          } catch (error) {
            setFormError(extractErrorMessage(error));
            setIsCompletingMapping(false);
          }
        },
        onError: (error: any) => {
          setFormError(extractErrorMessage(error));
          setIsCompletingMapping(false);
        },
      }
    );
  };

  const isMapping = isMappingExistingCosignProperty || isCompletingMapping;
  const isSubmitDisabled =
    isCosignPropertiesLoading ||
    isMapping ||
    !selectedCosignPropertyUuid ||
    !isSelectedPropertyVisible;

  const renderPropertyOption = (cosignProperty: ICosignProperty) => {
    const isSelected = selectedCosignPropertyUuid === cosignProperty.uuid;
    const address = getPropertyAddress(cosignProperty);

    return (
      <PropertyOptionButton
        key={cosignProperty.uuid}
        type="button"
        $selected={isSelected}
        onClick={() => setSelectedCosignPropertyUuid(cosignProperty.uuid)}
        data-testid={`cosign-property-option-${cosignProperty.uuid}`}
      >
        <PropertyOptionName>
          {cosignProperty.name || "Unnamed Cosign property"}
        </PropertyOptionName>
        {address && <PropertyOptionMeta>{address}</PropertyOptionMeta>}
        <PropertyOptionMeta>{cosignProperty.uuid}</PropertyOptionMeta>
      </PropertyOptionButton>
    );
  };

  return (
    <Modal
      visible={open}
      close={onCancel}
      afterClose={() => setFormError(null)}
      title="Map Existing Cosign Property"
      submitButtonLabel={
        isMapping ? "Mapping..." : "Map Property"
      }
      submit={handleSubmit}
      closeOnSubmit={false}
      submitting={isMapping}
      submitButtonDisabled={isSubmitDisabled}
      disableCancelLink={isMapping}
      closable={!isMapping}
      maskClosable={!isMapping}
      keyboard={!isMapping}
    >
      <Modal.Body>
        <PropertySummary>
          <PropertySummaryLabel>VERO property</PropertySummaryLabel>
          <PropertySummaryValue>{property.name}</PropertySummaryValue>
          {property.address && (
            <PropertyOptionMeta>{property.address}</PropertyOptionMeta>
          )}
        </PropertySummary>

        <PropertySearchInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name..."
          data-testid="cosign-property-search"
        />

        {cosignPropertiesError && (
          <CreatePropertyError data-testid="cosign-map-load-error">
            Unable to load Cosign properties. Please try again.
            <PropertyRetryButton
              type="button"
              onClick={() => refetchCosignProperties()}
            >
              Retry
            </PropertyRetryButton>
          </CreatePropertyError>
        )}

        {formError && (
          <CreatePropertyError data-testid="cosign-map-form-error">
            {formError}
          </CreatePropertyError>
        )}

        <PropertyOptionList>
          {isCosignPropertiesLoading && (
            <EmptyPropertyList>Loading Cosign properties...</EmptyPropertyList>
          )}

          {shouldShowPropertyOptions && cosignProperties.length === 0 && (
              <EmptyPropertyList>
                No properties found in Cosign for this account. Use 'Create in
                Cosign' instead.
              </EmptyPropertyList>
            )}

          {shouldShowPropertyOptions &&
            hasCosignProperties &&
            rankedProperties.length === 0 && (
              <EmptyPropertyList>
                No matching Cosign properties found.
              </EmptyPropertyList>
            )}

          {shouldShowPropertyOptions && bestMatch && (
              <PropertyOptionSection>
                <PropertyOptionSectionTitle>
                  Best match
                </PropertyOptionSectionTitle>
                {renderPropertyOption(bestMatch)}
              </PropertyOptionSection>
            )}

          {shouldShowPropertyOptions && otherProperties.length > 0 && (
              <PropertyOptionSection>
                <PropertyOptionSectionTitle>
                  Other properties
                </PropertyOptionSectionTitle>
                {otherProperties.map(renderPropertyOption)}
              </PropertyOptionSection>
            )}
        </PropertyOptionList>
      </Modal.Body>
    </Modal>
  );
};

export default MapExistingCosignPropertyModal;
